/**
 * tripSheet.ts — turn an ordered stop list into a day-by-day road sheet.
 *
 * Pure (no fetches) so it can be unit tested and shared by the saved-trip
 * view and the public share page. The day model mirrors
 * flask-service/trip_share_routes.py (normalise_kind / stop_nights /
 * drive_segments) — keep the two in step.
 *
 *   Day 1  Melbourne → Halls Gap        drive · night 1 of 2
 *   Day 2  Rest day at Halls Gap                night 2 of 2
 *   Day 3  Halls Gap → Mount Gambier    drive · night 1 of 1
 *   Day 4  Mount Gambier → Adelaide     drive · arrive
 */

import type { TripLeg, TripStop } from '../types/trip';
import type { SheetDrive, SheetFuelStop } from './tripApi';
import { dateForTripDay } from './tripPlanner';

export type SheetKind = 'start' | 'end' | 'via' | 'overnight' | 'camp';

export const BOOKED_TAG = 'booked';

/** Map legacy/variant kinds onto the shared contract. */
export function normaliseKind(kind?: string | null): SheetKind {
  const k = (kind || '').trim().toLowerCase();
  if (k === 'start' || k === 'end' || k === 'via' || k === 'overnight' || k === 'camp') return k;
  if (k === 'waypoint' || k === 'poi') return 'via';
  // 'accom' and anything else you can sleep at behaves like a campsite.
  return 'camp';
}

/**
 * Nights at a stop. Older clients posted nights=1 on EVERY stop (start/end
 * too), so the kind decides whether nights count. A camp with no nights field
 * (trips saved before nights were threaded through) is one night.
 */
export function stopNights(stop: Pick<TripStop, 'kind' | 'nights'>): number {
  const kind = normaliseKind(stop.kind);
  if (kind !== 'camp' && kind !== 'overnight') return 0;
  if (typeof stop.nights !== 'number' || Number.isNaN(stop.nights)) return 1;
  return Math.max(0, Math.floor(stop.nights));
}

/** True when the stop is a planned night with a real campsite attached. */
export function hasCampsite(stop: TripStop): boolean {
  return normaliseKind(stop.kind) === 'camp' && typeof stop.entityId === 'number';
}

export function isBooked(stop: TripStop): boolean {
  return (stop.tags || []).includes(BOOKED_TAG);
}

export function toggleBookedTags(tags: string[] | undefined, booked: boolean): string[] {
  const rest = (tags || []).filter((t) => t !== BOOKED_TAG);
  return booked ? [...rest, BOOKED_TAG] : rest;
}

/** [first, last] stop indexes of each day's drive. */
export function driveSegments(stops: TripStop[]): Array<[number, number]> {
  if (stops.length < 2) return [];
  const bounds = [0];
  for (let i = 1; i < stops.length - 1; i += 1) {
    if (stopNights(stops[i]) > 0) bounds.push(i);
  }
  bounds.push(stops.length - 1);
  const out: Array<[number, number]> = [];
  for (let i = 1; i < bounds.length; i += 1) {
    if (bounds[i] > bounds[i - 1]) out.push([bounds[i - 1], bounds[i]]);
  }
  return out;
}

export interface SheetNight {
  stop: TripStop;
  /** 1-based night at this stop, e.g. 2 of 2. */
  nightNumber: number;
  ofNights: number;
}

export interface SheetDay {
  /** 1-based. */
  dayNumber: number;
  /** YYYY-MM-DD, or null when the trip has no start date. */
  date: string | null;
  type: 'drive' | 'rest';
  from: TripStop;
  to: TripStop;
  vias: TripStop[];
  distanceKm?: number;
  driveMinutes?: number;
  /** false = straight-line estimate, not a routed road distance. */
  routed: boolean;
  fuelStops: SheetFuelStop[];
  fuelGapKm?: number;
  warnings: string[];
  night: SheetNight | null;
}

export interface BuildSheetOptions {
  startDate?: string | null;
  /** Fallback legs (straight-line) used when no routed drive matches. */
  legs?: TripLeg[];
  /** Routed per-day drives from GET /api/trips/<id>/sheet. */
  drives?: SheetDrive[] | null;
  driveHoursPerDay?: number | null;
  /**
   * The drive days as [from, to] stop indexes. Default: one per stretch
   * between nights (driveSegments). The shared trip page passes the
   * planner's own split (segmentIntoDays), so its days match the planner's.
   */
  segments?: Array<[number, number]>;
}

export function formatDrive(minutes?: number): string {
  if (typeof minutes !== 'number' || Number.isNaN(minutes)) return '';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h${m > 0 ? ` ${String(m).padStart(2, '0')}m` : ''}`;
}

export function formatKm(km?: number): string {
  if (typeof km !== 'number' || Number.isNaN(km)) return '';
  return `${Math.round(km).toLocaleString('en-AU')} km`;
}

/** Slack before we call a day long — nobody wants a nag over 10 minutes. */
const LONG_DAY_GRACE_MIN = 15;

export function buildSheetDays(stops: TripStop[], opts: BuildSheetOptions = {}): SheetDay[] {
  const { startDate = null, legs = [], drives = null, driveHoursPerDay = null, segments = null } = opts;
  if (stops.length === 0) return [];

  const driveByPair = new Map<string, SheetDrive>();
  (drives || []).forEach((d) => driveByPair.set(`${d.from_stop_id}>${d.to_stop_id}`, d));
  const legByFrom = new Map<string, TripLeg>();
  legs.forEach((l) => legByFrom.set(l.fromId, l));

  const days: SheetDay[] = [];
  let dayIndex = 0;

  const pushRestDays = (stop: TripStop, firstNight: number, total: number) => {
    for (let n = firstNight; n <= total; n += 1) {
      days.push({
        dayNumber: dayIndex + 1,
        date: dateForTripDay(startDate, dayIndex),
        type: 'rest',
        from: stop,
        to: stop,
        vias: [],
        routed: true,
        fuelStops: [],
        warnings: [],
        night: { stop, nightNumber: n, ofNights: total },
      });
      dayIndex += 1;
    }
  };

  // A trip that starts AT a campsite (legacy all-camp trips) spends its
  // nights there before the first drive.
  const firstNights = stopNights(stops[0]);
  if (firstNights > 0 && normaliseKind(stops[0].kind) !== 'start') {
    pushRestDays(stops[0], 1, firstNights);
  }

  for (const [a, b] of segments ?? driveSegments(stops)) {
    const from = stops[a];
    const to = stops[b];
    const isLast = b === stops.length - 1;
    const nights = isLast && normaliseKind(to.kind) === 'end' ? 0 : stopNights(to);

    const routed = driveByPair.get(`${from.id}>${to.id}`);
    let distanceKm: number | undefined;
    let driveMinutes: number | undefined;
    let isRouted = false;
    if (routed && typeof routed.distance_km === 'number') {
      distanceKm = routed.distance_km;
      driveMinutes = routed.duration_minutes ?? undefined;
      isRouted = routed.routed;
    } else {
      let km = 0;
      let min = 0;
      let known = true;
      for (let i = a; i < b; i += 1) {
        const leg = legByFrom.get(stops[i].id);
        if (typeof leg?.distanceKm !== 'number') known = false;
        km += leg?.distanceKm ?? 0;
        min += leg?.driveMinutes ?? 0;
      }
      if (known && b > a) {
        distanceKm = Number(km.toFixed(1));
        driveMinutes = Math.round(min);
      }
    }

    const warnings = [...(routed?.warnings || [])];
    const budget =
      typeof driveHoursPerDay === 'number' && driveHoursPerDay > 0 ? driveHoursPerDay * 60 : null;
    if (budget && typeof driveMinutes === 'number' && driveMinutes > budget + LONG_DAY_GRACE_MIN) {
      warnings.unshift(
        `Long day: ${formatDrive(driveMinutes)} on the road, over your ${driveHoursPerDay}h a day. Add an overnight to break it up.`
      );
    }

    days.push({
      dayNumber: dayIndex + 1,
      date: dateForTripDay(startDate, dayIndex),
      type: 'drive',
      from,
      to,
      vias: stops.slice(a + 1, b),
      distanceKm,
      driveMinutes,
      routed: isRouted,
      fuelStops: routed?.fuel_stops || [],
      fuelGapKm: routed?.fuel_gap?.gap_km,
      warnings,
      night: nights > 0 ? { stop: to, nightNumber: 1, ofNights: nights } : null,
    });
    dayIndex += 1;

    if (nights > 1) pushRestDays(to, 2, nights);
  }

  return days;
}

export interface SheetTotals {
  days: number;
  nights: number;
  distanceKm?: number;
  driveMinutes?: number;
  /** Nights with a campsite picked / booked. */
  campsPicked: number;
  campsBooked: number;
  allRouted: boolean;
}

export function summariseSheet(days: SheetDay[]): SheetTotals {
  let km = 0;
  let min = 0;
  let kmKnown = true;
  let nights = 0;
  let picked = 0;
  let booked = 0;
  let allRouted = true;
  for (const d of days) {
    if (d.type === 'drive') {
      if (typeof d.distanceKm === 'number') km += d.distanceKm;
      else kmKnown = false;
      min += d.driveMinutes ?? 0;
      if (!d.routed) allRouted = false;
    }
    if (d.night) {
      nights += 1;
      if (hasCampsite(d.night.stop)) picked += 1;
      if (isBooked(d.night.stop)) booked += 1;
    }
  }
  return {
    days: days.length,
    nights,
    distanceKm: kmKnown ? km : undefined,
    driveMinutes: kmKnown ? min : undefined,
    campsPicked: picked,
    campsBooked: booked,
    allRouted,
  };
}

/** "Fri 6 Nov" */
export function formatSheetDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
}

/** Days between today and an ISO date (negative = past). */
export function daysFromToday(iso: string | null, now: Date = new Date()): number | null {
  if (!iso) return null;
  const target = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
  return Math.round((target.getTime() - today.getTime()) / 86400000);
}
